import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Courier = () => {
  const [jobOptions, setJobOptions] = useState([]);

  // ------------------------------
  // Dynamic Form Field Config
  // BL Number (job_id_fk) comes first — every other field stays locked
  // until a job is picked, so the user has to select it before anything else.
  // ------------------------------
  const baseFormFields = useMemo(() => [
    { name: "job_id_fk", label: "BL Number", type: "select", required: true, col: 6, options: jobOptions },
    { name: "courier_type", label: "Courier Type", type: "select", required: true, col: 6,
      options: [
        { value: "Import Inward", label: "Import Inward" },
        { value: "Import Outward", label: "Import Outward" },
        { value: "Export Inward", label: "Export Inward" },
        { value: "Export Outward", label: "Export Outward" },
        { value: "Others", label: "Others" },
      ],
     },
    { name: "courier_service", label: "Courier Service", type: "text", col: 6 },
    { name: "type_of_document", label: "Type of Document", type: "text", col: 6 },
    { name: "courier_number", label: "Courier Number", type: "text", col: 6 },
    { name: "courier_date", label: "Courier Date", type: "date", col: 6 },
    { name: "received_date", label: "Received Date", type: "date", col: 6 },
    { name: "courier_from", label: "From", type: "text", col: 6 },
    { name: "courier_to", label: "To", type: "text", col: 6 },
    { name: "sender", label: "Sender", type: "text", col: 6 },
    { name: "receiver", label: "Receiver", type: "text", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: 0, label: "Active" },
        { value: 1, label: "Inactive" },
      ],
      col: 6,
    },
  ], [jobOptions]);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return baseFormFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { courier_id: 0 });
  }, [baseFormFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [courierList, setCourierList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // Every field except BL Number is locked until a BL is selected.
  const formFields = useMemo(() => {
    const locked = !formData.job_id_fk;
    return baseFormFields.map((field) =>
      field.name === "job_id_fk" ? field : { ...field, readOnly: locked, disabled: locked }
    );
  }, [baseFormFields, formData.job_id_fk]);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/masters/courier/list");
      if (response.status === 200) {
        setCourierList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  const fetchJobData = useCallback(async () => {
    try {
      const response = await post("/all_jobs/job/list");
      if (response.status === 200) {
        setJobOptions([
          { value: "", label: "Select BL Number" },
          ...response.data.map((item) => ({ value: item.job_id, label: item.bl_number })),
        ]);
      }
    } catch { /* silently ignore */ }
  }, []);

  useEffect(() => {
    fetchData();
    fetchJobData();
  }, [fetchData, fetchJobData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "BL Number", accessorKey: "bl_number" },
      { header: "Consignee Short Name", accessorKey: "consignee_code" },
      { header: "Courier Type", accessorKey: "courier_type" },
      { header: "Courier Service", accessorKey: "courier_service" },
      { header: "Type of Document", accessorKey: "type_of_document" },
      { header: "Courier Number", accessorKey: "courier_number" },
      { header: "Courier Date", accessorKey: "courier_date" },
      { header: "Received Date", accessorKey: "received_date" },
      { header: "From", accessorKey: "courier_from" },
      { header: "To", accessorKey: "courier_to" },
      { header: "Sender", accessorKey: "sender" },
      { header: "Receiver", accessorKey: "receiver" },
      { header: "Status", accessorKey: "status", label_condition: "status" },
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/masters/courier", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Courier</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add Courier
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Courier List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Courier List"
            columns={columns}
            data={courierList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Courier"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Courier;
